// QR Code Service for BookAPT
// Handles QR code generation and scanning for payments

import SimpleStorage from '../utils/simpleStorage';

export interface QRPaymentData {
  id: string;
  businessId: string;
  businessName: string;
  serviceName: string;
  amount: number;
  currency: string;
  description: string;
  paymentMethods: string[];
  expiresAt: string;
  createdAt: string;
  status: 'active' | 'used' | 'expired' | 'cancelled';
  usedAt?: string;
  customerId?: string;
  customerName?: string;
}

export interface QRCodeConfig {
  size: number;
  color: string;
  backgroundColor: string;
  logo?: string;
  logoSize?: number;
}

class QRCodeService {
  private readonly QR_PAYMENTS_KEY = 'qr_payments';
  private readonly QR_CONFIG_KEY = 'qr_config';

  // Generate QR code for payment
  async generatePaymentQR(paymentData: {
    businessId: string;
    businessName: string;
    serviceName: string;
    amount: number;
    description: string;
    paymentMethods: string[];
    expirationHours?: number;
  }): Promise<QRPaymentData> {
    try {
      const qrPayment: QRPaymentData = {
        id: `qr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        businessId: paymentData.businessId,
        businessName: paymentData.businessName,
        serviceName: paymentData.serviceName,
        amount: paymentData.amount,
        currency: 'USD',
        description: paymentData.description,
        paymentMethods: paymentData.paymentMethods,
        expiresAt: new Date(Date.now() + (paymentData.expirationHours || 24) * 60 * 60 * 1000).toISOString(),
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      // Save QR payment data
      await this.saveQRPayment(qrPayment);

      return qrPayment;
    } catch (error) {
      console.error('Error generating QR payment:', error);
      throw error;
    }
  }

  // Generate QR code data URL
  generateQRCodeDataURL(qrPayment: QRPaymentData): string {
    // In a real implementation, this would use a QR code library like qrcode
    // For demo purposes, we'll return a mock data URL
    const qrData = {
      type: 'payment',
      id: qrPayment.id,
      businessId: qrPayment.businessId,
      amount: qrPayment.amount,
      service: qrPayment.serviceName,
      expires: qrPayment.expiresAt
    };

    // Mock QR code data URL (in production, use actual QR code generation)
    const mockQRData = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==`;
    
    return mockQRData;
  }

  // Process QR code payment
  async processQRPayment(qrId: string, customerData: {
    customerId: string;
    customerName: string;
    paymentMethod: string;
    paymentData?: any;
  }): Promise<boolean> {
    try {
      const qrPayment = await this.getQRPayment(qrId);
      if (!qrPayment) {
        throw new Error('QR payment not found');
      }

      if (qrPayment.status !== 'active') {
        throw new Error('QR payment is not active');
      }

      if (new Date() > new Date(qrPayment.expiresAt)) {
        qrPayment.status = 'expired';
        await this.updateQRPayment(qrPayment);
        throw new Error('QR payment has expired');
      }

      // Process payment (integrate with existing payment service)
      const { paymentRequestService } = await import('./paymentRequestService');
      const success = await paymentRequestService.processPayment(
        qrId,
        customerData.paymentMethod,
        customerData.paymentData
      );

      if (success) {
        qrPayment.status = 'used';
        qrPayment.usedAt = new Date().toISOString();
        qrPayment.customerId = customerData.customerId;
        qrPayment.customerName = customerData.customerName;
        await this.updateQRPayment(qrPayment);
      }

      return success;
    } catch (error) {
      console.error('Error processing QR payment:', error);
      return false;
    }
  }

  // Get QR payment by ID
  async getQRPayment(qrId: string): Promise<QRPaymentData | null> {
    try {
      const qrPayments = await this.getQRPayments();
      return qrPayments.find(qr => qr.id === qrId) || null;
    } catch (error) {
      console.error('Error getting QR payment:', error);
      return null;
    }
  }

  // Get all QR payments
  async getQRPayments(): Promise<QRPaymentData[]> {
    try {
      const qrPaymentsJson = await SimpleStorage.getItem(this.QR_PAYMENTS_KEY);
      return qrPaymentsJson ? JSON.parse(qrPaymentsJson) : [];
    } catch (error) {
      console.error('Error getting QR payments:', error);
      return [];
    }
  }

  // Save QR payment
  private async saveQRPayment(qrPayment: QRPaymentData): Promise<void> {
    try {
      const qrPayments = await this.getQRPayments();
      qrPayments.push(qrPayment);
      
      await SimpleStorage.setItem(this.QR_PAYMENTS_KEY, JSON.stringify(qrPayments));
    } catch (error) {
      console.error('Error saving QR payment:', error);
    }
  }

  // Update QR payment
  private async updateQRPayment(qrPayment: QRPaymentData): Promise<void> {
    try {
      const qrPayments = await this.getQRPayments();
      const index = qrPayments.findIndex(qr => qr.id === qrPayment.id);
      if (index !== -1) {
        qrPayments[index] = qrPayment;
        
        await SimpleStorage.setItem(this.QR_PAYMENTS_KEY, JSON.stringify(qrPayments));
      }
    } catch (error) {
      console.error('Error updating QR payment:', error);
    }
  }

  // Get QR code configuration
  async getQRConfig(): Promise<QRCodeConfig> {
    try {
      const configJson = await SimpleStorage.getItem(this.QR_CONFIG_KEY);
      return configJson ? JSON.parse(configJson) : {
        size: 200,
        color: '#000000',
        backgroundColor: '#FFFFFF',
        logoSize: 40
      };
    } catch (error) {
      console.error('Error getting QR config:', error);
      return {
        size: 200,
        color: '#000000',
        backgroundColor: '#FFFFFF',
        logoSize: 40
      };
    }
  }

  // Update QR code configuration
  async updateQRConfig(config: QRCodeConfig): Promise<void> {
    try {
      await SimpleStorage.setItem(this.QR_CONFIG_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Error updating QR config:', error);
    }
  }

  // Get QR payment analytics
  async getQRAnalytics(): Promise<{
    totalGenerated: number;
    totalUsed: number;
    totalExpired: number;
    conversionRate: number;
    averageAmount: number;
    totalRevenue: number;
  }> {
    try {
      const qrPayments = await this.getQRPayments();
      
      const analytics = {
        totalGenerated: qrPayments.length,
        totalUsed: qrPayments.filter(qr => qr.status === 'used').length,
        totalExpired: qrPayments.filter(qr => qr.status === 'expired').length,
        conversionRate: 0,
        averageAmount: 0,
        totalRevenue: 0
      };

      if (analytics.totalGenerated > 0) {
        analytics.conversionRate = (analytics.totalUsed / analytics.totalGenerated) * 100;
      }

      if (qrPayments.length > 0) {
        analytics.averageAmount = qrPayments.reduce((sum, qr) => sum + qr.amount, 0) / qrPayments.length;
      }

      analytics.totalRevenue = qrPayments
        .filter(qr => qr.status === 'used')
        .reduce((sum, qr) => sum + qr.amount, 0);

      return analytics;
    } catch (error) {
      console.error('Error getting QR analytics:', error);
      return {
        totalGenerated: 0,
        totalUsed: 0,
        totalExpired: 0,
        conversionRate: 0,
        averageAmount: 0,
        totalRevenue: 0
      };
    }
  }
}

export const qrCodeService = new QRCodeService();
export default QRCodeService;

